import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Book, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { TeamBApi, type Plant, type TimelineResult, formatDateTime } from '@ai-tiaozs/shared';
import { SafeImage } from '@/components/SafeImage';
import { ObservationAiSummary } from '@/components/ObservationAiSummary';

function readLastPlantHint(): string | null {
  const raw = sessionStorage.getItem('b_last_observation');
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as { plantId?: string }).plantId ?? null;
  } catch {
    return null;
  }
}

/**
 * B 队 · ④ 养护日记(华睿)
 * 以「植物」为大项:先选植物,再看该植物下的拍照日记
 */
export function TeamBGrowthTimeline() {
  const [params] = useSearchParams();
  const plantIdFromUrl = params.get('plantId');

  const [plants, setPlants] = useState<Plant[]>([]);
  const [plantId, setPlantId] = useState('');
  const [data, setData] = useState<TimelineResult | null>(null);
  const [loadingPlants, setLoadingPlants] = useState(true);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrichingIds, setEnrichingIds] = useState<Set<string>>(new Set());

  const enrichTimelineSnapshots = useCallback(async (days: NonNullable<typeof data>['days']) => {
    for (const o of days) {
      if (!o.observationId || o.aiSnapshot) continue;
      const id = o.observationId;
      setEnrichingIds((prev) => new Set(prev).add(id));
      try {
        const ctx = await TeamBApi.getObservation(id);
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            days: prev.days.map((d) =>
              d.observationId === id ? { ...d, aiSnapshot: ctx.aiSnapshot ?? null } : d,
            ),
          };
        });
      } catch {
        // ignore
      } finally {
        setEnrichingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }
  }, []);

  const loadTimeline = useCallback(async (pid: string) => {
    setLoadingTimeline(true);
    setError(null);
    try {
      const r = await TeamBApi.timeline(pid);
      setData(r);
      void enrichTimelineSnapshots(r.days);
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoadingTimeline(false);
    }
  }, [enrichTimelineSnapshots]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPlants(true);
      try {
        const { plants: list } = await TeamBApi.listPlants();
        if (cancelled) return;
        setPlants(list);
        const hint = readLastPlantHint();
        const initial =
          (plantIdFromUrl && list.some((p) => p.id === plantIdFromUrl) ? plantIdFromUrl : null) ??
          (hint && list.some((p) => p.id === hint) ? hint : null) ??
          list[0]?.id ??
          '';
        if (initial) {
          setPlantId(initial);
          await loadTimeline(initial);
        }
      } catch {
        if (!cancelled) setError('加载植物列表失败');
      } finally {
        if (!cancelled) setLoadingPlants(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plantIdFromUrl, loadTimeline]);

  const onPlantChange = async (pid: string) => {
    setPlantId(pid);
    setData(null);
    await loadTimeline(pid);
  };

  const selectedPlant = plants.find((p) => p.id === plantId);

  const abnormalCount =
    data?.days.filter(
      (d) =>
        d.adviceSummary?.includes('调整') ||
        d.adviceSummary?.includes('处理') ||
        d.adviceSummary?.includes('检查'),
    ).length ?? 0;
  const healthyCount = (data?.days.length ?? 0) - abnormalCount;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-lg font-bold">
          <Book className="h-5 w-5 text-teamB-primary" /> 养护日记
        </h1>
        <p className="text-xs text-slate-500">
          日记按植物归档 — 先选一株,再看它的拍照记录与健康档案
        </p>
      </header>

      {loadingPlants && (
        <p className="flex items-center gap-1 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          加载植物列表…
        </p>
      )}

      {!loadingPlants && plants.length === 0 && (
        <div className="card space-y-2 text-sm text-slate-600">
          <p>还没有植物,无法查看日记。</p>
          <Link to="/add" className="btn-primary inline-flex w-full justify-center">
            去添加植物
          </Link>
        </div>
      )}

      {plants.length > 0 && (
        <section className="card space-y-2">
          <label className="text-xs text-slate-500">① 选择植物</label>
          <select
            className="input"
            value={plantId}
            onChange={(e) => void onPlantChange(e.target.value)}
          >
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {p.location}
              </option>
            ))}
          </select>
          {selectedPlant && (
            <p className="text-[11px] text-slate-500">
              {selectedPlant.species}
              {data ? ` · 共 ${data.totalDays} 条拍照记录` : ''}
            </p>
          )}
        </section>
      )}

      {loadingTimeline && (
        <p className="flex items-center gap-1 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          加载 {selectedPlant?.name ?? '植物'} 的日记…
        </p>
      )}

      {error && !loadingTimeline && (
        <p className="text-xs text-teamA-danger">{error}</p>
      )}

      {data && selectedPlant && !loadingTimeline && (
        <>
          <section className="card bg-teamB-primary/5 space-y-2">
            <h2 className="text-sm font-semibold text-teamB-primary">
              📊 {selectedPlant.name} · 健康档案
            </h2>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-md bg-white p-2">
                <p className="text-slate-500">拍照记录</p>
                <p className="text-lg font-bold">{data.totalDays}</p>
              </div>
              <div className="rounded-md bg-white p-2">
                <p className="text-emerald-600">健康</p>
                <p className="text-lg font-bold text-emerald-600">{healthyCount}</p>
              </div>
              <div className="rounded-md bg-white p-2">
                <p className="text-teamA-danger">异常</p>
                <p className="text-lg font-bold text-teamA-danger">{abnormalCount}</p>
              </div>
            </div>
          </section>

          {data.days.length === 0 ? (
            <div className="card space-y-2 text-sm text-slate-600">
              <p>「{selectedPlant.name}」还没有带照片的观察记录。</p>
              <Link
                to={`/daily?plantId=${plantId}`}
                className="text-teamB-primary underline"
              >
                去为这株植物拍照记录
              </Link>
            </div>
          ) : (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-slate-700">
                ② {selectedPlant.name} 的拍照日记
              </h2>
              <ol className="space-y-3">
                {data.days.map((d) => {
                  const isAbnormal =
                    d.adviceSummary?.includes('调整') ||
                    d.adviceSummary?.includes('处理') ||
                    d.adviceSummary?.includes('检查');
                  const recordIndex = d.recordIndex;
                  return (
                    <li key={d.observationId ?? `${recordIndex}-${d.createdAt}`} className="card">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="badge bg-teamB-primary/10 text-teamB-primary">
                          {d.createdAt ? formatDateTime(d.createdAt) : '未知时间'}
                        </span>
                        <span className="text-[11px] text-slate-500">第 {recordIndex} 次记录</span>
                        {d.speciesMismatch && (
                          <span className="badge flex items-center gap-0.5 bg-teamA-warning/15 text-teamA-warning">
                            <AlertCircle className="h-3 w-3" />
                            照片像{d.speciesMismatch.detected},与档案({d.speciesMismatch.expected})不符
                          </span>
                        )}
                        {!d.speciesMismatch &&
                          (isAbnormal ? (
                            <span className="badge flex items-center gap-0.5 bg-teamA-danger/10 text-teamA-danger">
                              <AlertCircle className="h-3 w-3" /> 异常
                            </span>
                          ) : (
                            <span className="badge flex items-center gap-0.5 bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="h-3 w-3" /> 健康
                            </span>
                          ))}
                      </div>
                      <div className="mt-2 flex max-h-72 w-full items-center justify-center overflow-hidden rounded-md bg-slate-50 ring-1 ring-slate-200">
                        <SafeImage
                          team="b"
                          imageUrl={d.photoUrl}
                          imageKey={d.photoKey}
                          alt={`${selectedPlant.name} ${d.createdAt ? formatDateTime(d.createdAt) : ''}`}
                          className="max-h-72 w-full object-contain"
                          placeholderClassName="flex h-32 w-full items-center justify-center text-xs text-slate-400"
                        />
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-slate-600">
                        <div>
                          <p className="text-slate-400">土壤</p>
                          <p>{d.soil ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">光照</p>
                          <p>{d.lightPosition ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">叶片</p>
                          <p>{d.leafState ?? '—'}</p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <ObservationAiSummary
                          snapshot={d.aiSnapshot}
                          compact
                          loading={d.observationId ? enrichingIds.has(d.observationId) : false}
                        />
                      </div>
                      {d.careActions.length > 0 && (
                        <p className="mt-2 text-xs text-slate-500">
                          护理记录:
                          {d.careActions
                            .map((a) => (a === '完成今日任务' ? '✅ 今日任务已完成' : a))
                            .join('、')}
                        </p>
                      )}
                      {d.adviceSummary && (
                        <div className="mt-2 rounded-md bg-teamB-primary/5 px-2 py-1 text-xs text-teamB-primary">
                          🩺 AI 当时建议:{d.adviceSummary}
                        </div>
                      )}
                      {d.observationId && (
                        <Link
                          to={`/advice?obsId=${d.observationId}`}
                          className="mt-2 inline-block text-xs text-teamB-primary underline"
                        >
                          查看这条记录的 AI 诊断 →
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          <Link
            to={`/report?plantId=${plantId}`}
            className="btn-primary w-full"
          >
            📊 查看 {selectedPlant.name} 的健康报告 →
          </Link>
        </>
      )}
    </div>
  );
}
